import React, {
  FunctionComponent, useCallback, useMemo, useState,
} from 'react';
import {
  Action, ConfirmModal, ICON_ACCOUNT_MULTIPLE_PLUS_OUTLINE, Portal, Tooltip, useModal, useNotifications,
} from '@doveit/bricks';
import { JobApplicationStatus } from '../../../domain/types/jobApplicationStatus';
import { JobApplicationSales, JobApplication } from '../../../providers/api/dtos';
import { JobApplicationFilters, updateJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';
import { activeStatus, isRestorable } from '../../../utils/job-application/getJobApplicationProgress';
import { useJobApplications } from '../../hooks/use-job-applications/useJobApplications';
import { JobApplicationType } from '../../../domain/types';
import { MAX_PAGED_SIZE } from '../../../constants';

interface ChildFnProps {
  disabled?: boolean,
  restore?: VoidFunction,
  loading?: boolean,
}

export interface RestoreJobApplicationProps {
  jobApplication: JobApplication,
  onSuccess?: (jobApplication: JobApplication) => void,
  children?: (props: ChildFnProps) => React.ReactNode,
}

export const RESTORE_JOB_APPLICATION_TOOLTIP_MESSAGE = 'Non è possibile ripristinare la candidatura perché esiste già una candidatura attiva per questo candidato';
export const RESTORE_JOB_APPLICATION_WARNING_MESSAGE = 'Confermi di voler ripristinare la candidatura?';
export const RESTORE_JOB_APPLICATION_SUCCESS_MESSAGE = 'Candidatura ripristinata con successo';
export const RESTORE_JOB_APPLICATION_ERROR_MESSAGE = 'Non è stato possibile ripristinare la candidatura';

const RestoreJobApplicationAction: FunctionComponent<RestoreJobApplicationProps> = ({
  jobApplication,
  onSuccess,
  children,
}) => {
  const restoreModal = useModal();
  const [isSaving, setIsSaving] = useState(false);
  const { addSuccess, addError } = useNotifications();

  const isRestorableJobApplication = useMemo(
    () => (jobApplication?.status ? isRestorable(jobApplication?.status) : undefined),
    [jobApplication?.status],
  );

  const salesJobApplication: JobApplicationSales | undefined = jobApplication.applicationType === JobApplicationType.SALES ? jobApplication as JobApplicationSales : undefined;
  const filters: JobApplicationFilters | null = React.useMemo(() => ({
    applicantId: jobApplication.applicantId,
    status: activeStatus,
    jobPositionId: jobApplication.jobPositionId,
    province: salesJobApplication?.provinces,
  }), [jobApplication.applicantId, jobApplication.jobPositionId, salesJobApplication?.provinces]);

  const { data: activeJobApplications, error: activeJobApplicationsError } = useJobApplications(filters, { size: MAX_PAGED_SIZE });
  const anActiveJobApplicationAlreadyExists = React.useMemo(() => (activeJobApplications?.content.length || 0) > 0, [activeJobApplications]);

  const onConfirm = useCallback(async () => {
    setIsSaving(true);

    try {
      const updatedJobApplication = await updateJobApplication(
        jobApplication.id!,
        {
          ...jobApplication,
          status: JobApplicationStatus.IN_PROGRESS,
        },
      );

      setIsSaving(false);
      restoreModal.close();
      addSuccess(RESTORE_JOB_APPLICATION_SUCCESS_MESSAGE);

      if (onSuccess) {
        onSuccess(updatedJobApplication);
      }
    } catch (error) {
      setIsSaving(false);
      addError(RESTORE_JOB_APPLICATION_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, restoreModal, jobApplication, onSuccess]);

  if (!isRestorableJobApplication || activeJobApplicationsError) {
    return null;
  }

  if (anActiveJobApplicationAlreadyExists) {
    return children ? (
      <Tooltip content={RESTORE_JOB_APPLICATION_TOOLTIP_MESSAGE}>
        <div>
          {children({ disabled: true })}
        </div>
      </Tooltip>
    ) : (
      <Tooltip content={RESTORE_JOB_APPLICATION_TOOLTIP_MESSAGE}>
        <div>
          <Action
            label="Ripristina"
            emphasis="high"
            disabled
          />
        </div>
      </Tooltip>
    );
  }

  return (
    <>
      {children
        ? children({ restore: restoreModal.open, loading: isSaving })
        : (
          <Action
            aria-label="Azione per ripristinare la candidatura"
            label="Ripristina"
            onClick={restoreModal.open}
            iconLeft={{ path: ICON_ACCOUNT_MULTIPLE_PLUS_OUTLINE }}
            color="success"
            emphasis="high"
          />
        )}
      <Portal>
        <ConfirmModal
          isOpen={restoreModal.isOpen}
          aria-label="Modale di conferma ripristino candidatura"
          title="Ripristina candidatura"
          onConfirm={onConfirm}
          onAbort={restoreModal.close}
          loading={isSaving}
        >
          {RESTORE_JOB_APPLICATION_WARNING_MESSAGE}
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default RestoreJobApplicationAction;
